import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { IconDownload, IconAlertTriangle } from "@tabler/icons-react";
import { confirmGenerateDespiteNonAbusiveRate } from "utils/rate-abusiveness";

const ContractDownloadPackModal = ({ open, onClose, contract }) => {
  const [customerFiles, setCustomerFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [includeRepeticao, setIncludeRepeticao] = useState(true);
  const [includeCalculo, setIncludeCalculo] = useState(true);
  const [includeHonorarios, setIncludeHonorarios] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [packWarnings, setPackWarnings] = useState([]);

  useEffect(() => {
    if (open && contract?.customer?.id) {
      setLoadingFiles(true);
      fetch(`/backend/customer-files/?customer_id=${contract.customer.id}&page_size=500`)
        .then((res) => res.json())
        .then((data) => {
          const files = data.results || data;
          setCustomerFiles(Array.isArray(files) ? files : []);
          setSelectedFileIds(new Set((Array.isArray(files) ? files : []).map((f) => f.id)));
        })
        .catch(() => setCustomerFiles([]))
        .finally(() => setLoadingFiles(false));
    }
  }, [open, contract?.customer?.id]);

  useEffect(() => {
    if (open) {
      setIncludeRepeticao(true);
      setIncludeCalculo(true);
      setIncludeHonorarios(true);
      setPackWarnings([]);
    }
  }, [open]);

  const handleToggleFile = (id) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDownload = async () => {
    if (!contract) return;
    if (!includeRepeticao && !includeCalculo && !includeHonorarios && selectedFileIds.size === 0) {
      alert("Selecione pelo menos um item para incluir no pack.");
      return;
    }

    if ((includeRepeticao || includeCalculo) && !confirmGenerateDespiteNonAbusiveRate(contract)) {
      return;
    }

    setLoading(true);
    setPackWarnings([]);
    try {
      const response = await fetch(`/backend/contracts/${contract.id}/download-pack/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_file_ids: Array.from(selectedFileIds),
          include_repeticao_indebito: includeRepeticao,
          include_calculo_inicial: includeCalculo,
          include_honorarios: includeHonorarios,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Erro ao gerar o pack.");
      }

      const warningsHeader = response.headers.get("X-Pack-Warnings");
      const warnings = warningsHeader ? JSON.parse(warningsHeader) : [];

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pack_contrato_${contract.number || contract.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (warnings.length > 0) {
        setPackWarnings(warnings);
      } else {
        onClose();
      }
    } catch (e) {
      alert(e.message || "Erro ao gerar o pack. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const hasWarnings = packWarnings.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Download do pack do contrato</DialogTitle>
      <DialogContent>
        {hasWarnings ? (
          <>
            <Alert severity="warning" icon={<IconAlertTriangle size={20} />} sx={{ mb: 2 }}>
              <AlertTitle>Pack gerado com avisos</AlertTitle>
              O download foi realizado, mas {packWarnings.length === 1 ? "o seguinte arquivo não pôde ser incluído" : "os seguintes arquivos não puderam ser incluídos"}:
            </Alert>
            <List dense disablePadding>
              {packWarnings.map((w, idx) => (
                <React.Fragment key={idx}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {w.file}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {w.reason}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < packWarnings.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selecione os arquivos e documentos que deseja incluir no pack. O sistema gerará um ZIP com todos os itens selecionados.
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Arquivos do cliente
            </Typography>
            {loadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : customerFiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Nenhum anexo cadastrado para este cliente.
              </Typography>
            ) : (
              <FormGroup sx={{ pl: 0 }}>
                {customerFiles.map((f) => (
                  <FormControlLabel
                    key={f.id}
                    control={
                      <Checkbox
                        checked={selectedFileIds.has(f.id)}
                        onChange={() => handleToggleFile(f.id)}
                      />
                    }
                    label={f.title || `Arquivo ${f.id}`}
                  />
                ))}
              </FormGroup>
            )}
            <FormGroup sx={{ pl: 0, mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeHonorarios}
                    onChange={(e) => setIncludeHonorarios(e.target.checked)}
                  />
                }
                label="Honorários (DOCX)"
              />
            </FormGroup>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Documentos do contrato
            </Typography>
            <FormGroup sx={{ pl: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeRepeticao}
                    onChange={(e) => setIncludeRepeticao(e.target.checked)}
                  />
                }
                label="Repetição do indébito (DOCX)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCalculo}
                    onChange={(e) => setIncludeCalculo(e.target.checked)}
                  />
                }
                label="Cálculo da inicial (HTML)"
              />
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {hasWarnings ? (
          <Button variant="contained" onClick={onClose}>
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="secondary">
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={loading || (!includeRepeticao && !includeCalculo && !includeHonorarios && selectedFileIds.size === 0)}
              startIcon={loading ? <CircularProgress size={16} /> : <IconDownload size={18} />}
            >
              {loading ? "Gerando..." : "Gerar e baixar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContractDownloadPackModal;
